import React from "react";

// Shared "what photo to upload" UI for the Member/Student and Coach/Club
// registration forms — kept in one place so both pages show the identical
// requirements list and reference photo. Reference image is a static asset
// (not a real member's photo) used purely to illustrate framing/background.
export const PHOTO_EXAMPLE_SRC = "/images/registration/photo-example.png";

const PHOTO_REQUIREMENTS = [
  "Clear, front-facing photo",
  "Plain, light background",
  "Face should be clearly visible",
  "No sunglasses, hats or face coverings",
  "Image should not be blurry",
];

const PHOTO_SPEC_CHIPS = [
  {
    label: "Min 300×300px",
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 8V4h4M20 8V4h-4M4 16v4h4m12-4v4h-4"
      />
    ),
  },
  {
    label: "Max 2 MB",
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6M13 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8l-6-5z M13 3v5h5"
      />
    ),
  },
  {
    label: "PNG / JPG",
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M4 6h16a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 011-1zM10 9a1 1 0 11-2 0 1 1 0 012 0z"
      />
    ),
  },
];

function CheckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

// Middle "Photo Requirements" panel — checklist plus a footer row of
// dimension/size/format chips. Used inline on the Student/Member form
// (there's room for the full three-column layout there) and stacked inside
// PhotoExampleModal for the Coach form's narrower per-coach cards.
export function PhotoRequirementsPanel({ className = "" }) {
  return (
    <div className={`bg-gray-50 border border-gray-200 rounded-xl p-4 ${className}`}>
      <p className="flex items-center gap-1.5 text-sm font-semibold text-blue-700">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        Photo Requirements
      </p>
      <ul className="mt-3 space-y-1.5">
        {PHOTO_REQUIREMENTS.map((r) => (
          <li key={r} className="flex items-center gap-2 text-sm text-gray-700">
            <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
            {r}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 pt-3 border-t border-gray-200">
        {PHOTO_SPEC_CHIPS.map((chip) => (
          <span key={chip.label} className="flex items-center gap-1.5 text-xs text-gray-500">
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {chip.icon}
            </svg>
            {chip.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// Right-hand "Example Photo" card — the reference image with a caption.
export function ExamplePhotoCard({ className = "" }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-xl p-4 flex flex-col items-center text-center ${className}`}>
      <p className="text-sm font-semibold text-gray-800">Example Photo</p>
      <img
        src={PHOTO_EXAMPLE_SRC}
        alt="Example of an acceptable profile photo: front-facing, plain light background"
        className="w-28 h-32 object-cover rounded-lg border border-gray-200 mt-3"
      />
      <p className="text-xs text-gray-500 mt-3">This is an example of an acceptable photo.</p>
    </div>
  );
}

// Small text/icon trigger for opening PhotoExampleModal — used on both forms
// so the entry point looks the same everywhere, even where the full panel +
// example card don't fit inline (the Coach form's per-coach cards).
export function ViewPhotoExampleLink({ onClick, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline ${className}`}
    >
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
      </svg>
      View Photo Example
    </button>
  );
}

// Modal opened by ViewPhotoExampleLink — same requirements panel + example
// card as the inline layout, just stacked into a dialog for pages/sections
// too narrow to show them side by side.
export function PhotoExampleModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <ExamplePhotoCard />
        <PhotoRequirementsPanel className="mt-4" />
      </div>
    </div>
  );
}
